<template>
  <div class="flex flex-col gap-4 text-left">
    <div v-for="group in groups" :key="group.title" class="flex flex-col">
      <div class="flex items-center justify-between mb-3.5">
        <span class="text-base font-bold text-primary30">{{ group.title }}</span>
        <span class="text-sm text-[#52526f]">{{ group.countLabel }}</span>
      </div>

      <!-- columns = 2: grid (2 colunas a partir de lg), cada item com borda inferior -->
      <div :class="columns === 2 ? 'grid grid-cols-1 lg:grid-cols-2 gap-x-10' : 'contents'">
        <template v-for="(invite, index) in group.items" :key="invite.id">
          <div
            class="flex items-start gap-3"
            :class="{ 'py-[14px] border-b border-[#e0e0e0]': columns === 2 }"
          >
            <div
              class="relative shrink-0"
              :style="{ width: SVG_SIZE + 'px', height: SVG_SIZE + 'px' }"
            >
              <svg
                :width="SVG_SIZE"
                :height="SVG_SIZE"
                class="absolute top-0 left-0 -rotate-90"
                aria-hidden="true"
              >
                <circle
                  :cx="SVG_SIZE / 2"
                  :cy="SVG_SIZE / 2"
                  :r="RADIUS"
                  :stroke="NEUTRAL_RING_COLOR"
                  :stroke-width="STROKE_WIDTH"
                  fill="none"
                />
                <circle
                  v-if="getClampedScore(invite.doctor.npsScore) > 0"
                  :cx="SVG_SIZE / 2"
                  :cy="SVG_SIZE / 2"
                  :r="RADIUS"
                  :stroke="getArcColor(invite.doctor.npsScore)"
                  :stroke-width="STROKE_WIDTH"
                  fill="none"
                  :stroke-dasharray="CIRCUMFERENCE"
                  :stroke-dashoffset="getArcOffset(invite.doctor.npsScore)"
                  stroke-linecap="round"
                />
              </svg>
              <div
                class="absolute rounded-full flex items-center justify-center text-sm font-semibold text-white"
                :style="{ ...INNER_STYLE, backgroundColor: getAvatarColor(invite.doctor.name) }"
              >
                {{ getDoctorInitials(invite.doctor.name) }}
              </div>
            </div>
            <div class="flex-1 flex flex-col gap-0.5 min-w-0 overflow-hidden">
              <span class="text-sm font-semibold text-[#222]">{{
                getFirstAndSecondName(invite.doctor.name)
              }}</span>
              <span class="text-xs text-[#52526f]">CRM {{ invite.doctor.crm }}</span>
              <span v-if="invite.acceptedAt" class="text-xs text-[#52526f]">
                Aceitou em {{ formatAcceptedAt(invite.acceptedAt) }}
              </span>
              <span
                v-if="getExpirationLabel(invite)"
                class="text-xs font-medium text-[#a15c07]"
                data-testid="candidate-expiration"
              >
                {{ getExpirationLabel(invite) }}
              </span>
              <span v-if="invite.declineReason" class="text-xs text-[#999] italic">
                Motivo: {{ invite.declineReason }}
              </span>
            </div>
            <div class="shrink-0">
              <span
                class="inline-block px-2 py-[3px] rounded-[10px] text-[11px] font-medium whitespace-nowrap"
                :class="getStatusBadgeClasses(invite)"
              >
                {{ getStatusLabel(invite) }}
              </span>
            </div>
          </div>

          <!-- columns = 1: separador entre itens empilhados (visual original do modal) -->
          <template v-if="columns === 1 && index < group.items.length - 1">
            <div class="h-3.5" />
            <div class="h-px bg-[#e0e0e0]" />
            <div class="h-3.5" />
          </template>
        </template>
      </div>
    </div>

    <div
      v-if="!groups.length"
      class="flex items-center justify-center py-[32px] text-sm text-[#7f7f8e]"
      data-testid="candidates-list-empty"
    >
      Nenhum convidado encontrado para esta troca.
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { differenceInDays, differenceInHours, format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import type { InviteResponseV2 } from '@/Network/Types/Responses/HandoverInviteResponses'
import { getFirstAndSecondName } from '@/utils/getFirstAndSecondName'
import { getDoctorInitials } from '@/utils/string'
import { InviteStatus, InviteOrigin } from '@/Network/Types/Requests/HandoverInviteRequests'
import { classifyNpsScore, NEUTRAL_RING_COLOR } from '@/utils/nps'

const props = withDefaults(
  defineProps<{
    invites: InviteResponseV2[]
    /** 1 = itens empilhados (modal); 2 = grid de 2 colunas em lg (página de detalhes) */
    columns?: 1 | 2
  }>(),
  { columns: 1 }
)

const AVATAR_SIZE = 40
const STROKE_WIDTH = 4
const RADIUS = AVATAR_SIZE / 2 + STROKE_WIDTH / 2
const SVG_SIZE = RADIUS * 2 + STROKE_WIDTH
const CIRCUMFERENCE = 2 * Math.PI * RADIUS
const INNER_STYLE = {
  width: AVATAR_SIZE + 'px',
  height: AVATAR_SIZE + 'px',
  top: STROKE_WIDTH + 'px',
  left: STROKE_WIDTH + 'px'
}

function getClampedScore(score?: number | null): number {
  if (score == null) return 0
  return Math.min(100, Math.max(0, score))
}

function getArcOffset(score?: number | null): number {
  return CIRCUMFERENCE * (1 - getClampedScore(score) / 100)
}

function getArcColor(score?: number | null): string {
  if (score == null) return NEUTRAL_RING_COLOR
  return classifyNpsScore(score).color
}

const directInvites = computed(() => props.invites.filter((i) => i.origin === InviteOrigin.DIRECT))

const showcaseInvites = computed(() =>
  props.invites.filter((i) => i.origin === InviteOrigin.SHOWCASE)
)

// Grupos com convites, na ordem de exibição; grupo vazio não aparece.
const groups = computed(() =>
  [
    {
      title: 'Convidados diretos',
      items: directInvites.value,
      countLabel: `${directInvites.value.length} ${
        directInvites.value.length === 1 ? 'convidado' : 'convidados'
      }`
    },
    {
      title: 'Candidatos da vitrine',
      items: showcaseInvites.value,
      countLabel: `${showcaseInvites.value.length} ${
        showcaseInvites.value.length === 1 ? 'candidato' : 'candidatos'
      }`
    }
  ].filter((group) => group.items.length)
)

const acceptedDirectQueue = computed(() =>
  directInvites.value
    .filter((i) => i.status === InviteStatus.ACCEPTED)
    .sort((a, b) => {
      if (!a.acceptedAt || !b.acceptedAt) return 0
      return new Date(a.acceptedAt).getTime() - new Date(b.acceptedAt).getTime()
    })
)

function getQueuePosition(invite: InviteResponseV2): number {
  return acceptedDirectQueue.value.findIndex((i) => i.id === invite.id) + 1
}

const AVATAR_COLORS = [
  '#4778c7',
  '#804db2',
  '#995933',
  '#2e7d32',
  '#c62828',
  '#00838f',
  '#ad1457',
  '#4527a0',
  '#ef6c00',
  '#1565c0'
]

function getAvatarColor(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

function formatAcceptedAt(dateStr: string): string {
  try {
    const date = parseISO(dateStr)
    return format(date, "EEE dd/MM 'às' HH'h'mm", { locale: ptBR })
  } catch {
    return dateStr
  }
}

/**
 * Prazo restante do convite, só para quem ainda pode responder. Um convite já
 * respondido ou já vencido tem o próprio status contando a história — repetir o
 * prazo ali só competiria com ele.
 */
function getExpirationLabel(invite: InviteResponseV2): string | null {
  if (invite.status !== InviteStatus.PENDING || !invite.expiresAt) return null

  try {
    const expiresAt = parseISO(invite.expiresAt)
    const hours = differenceInHours(expiresAt, new Date())
    if (hours < 0) return null
    if (hours < 24) return `Expira em ${hours} ${hours === 1 ? 'hora' : 'horas'}`

    const days = differenceInDays(expiresAt, new Date())
    return `Expira em ${days} ${days === 1 ? 'dia' : 'dias'}`
  } catch {
    return null
  }
}

function getStatusLabel(invite: InviteResponseV2): string {
  if (invite.status === InviteStatus.ACCEPTED) {
    const pos = getQueuePosition(invite)
    return pos > 0 ? `${pos}º na fila` : 'Aceito'
  }
  if (invite.status === InviteStatus.PENDING) return 'Aguardando'
  if (invite.status === InviteStatus.DECLINED) return 'Recusou'
  if (invite.status === InviteStatus.EXPIRED) return 'Expirado'
  if (invite.status === InviteStatus.CANCELLED) return 'Cancelado'
  return invite.status
}

function getStatusBadgeClasses(invite: InviteResponseV2): string {
  if (invite.status === InviteStatus.ACCEPTED) {
    return 'bg-primary5 text-[#235e6c]'
  }
  return 'bg-[#e2e2e9] text-[#999]'
}
</script>
